import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { 
  Sparkles, Save, AlertCircle, CheckCircle, 
  Globe, MessageSquare, Users, Zap, Shield,
  ChevronRight, X, Info, Plus
} from 'lucide-react';
import './AiConfig.css';

const AiConfig = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    isEnabled: false,
    businessContext: '',
    faqText: '',
    escalationKeywords: 'agent,human,msaada,mtu,help,talk to agent,speak to human,complaint,return,refund,order status,cancel order',
    responseLanguage: 'auto'
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/ai-config');
      if (response.data && response.data.id) {
        setConfig({
          isEnabled: response.data.isEnabled || false,
          businessContext: response.data.businessContext || '',
          faqText: response.data.faqText || '',
          escalationKeywords: response.data.escalationKeywords || 'agent,human,msaada,mtu,help,talk to agent,speak to human,complaint,return,refund,order status,cancel order',
          responseLanguage: response.data.responseLanguage || 'auto'
        });
      }
    } catch (err) {
      console.error('Error fetching AI config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    setShowSuccess(false);

    try {
      await api.post('/api/ai-config', config);
      setMessage('✅ AI configuration saved successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      setError('❌ ' + (err.response?.data?.message || 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await api.patch('/api/ai-config/toggle');
      setConfig({ ...config, isEnabled: response.data.isEnabled });
      setMessage(response.data.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError('❌ Failed to toggle AI');
    }
  };

  if (loading) {
    return (
      <div className="ai-loading">
        <div className="ai-spinner"></div>
        <p>Loading AI configuration...</p>
      </div>
    );
  }

  return (
    <div className="ai-config-page">
      {/* Header */}
      <div className="ai-header">
        <div>
          <h1 className="ai-title">
            <Sparkles size={28} /> AI Assistant
          </h1>
          <p className="ai-subtitle">
            Configure your AI assistant to automatically reply to customer WhatsApp messages
          </p>
        </div>
        <button 
          className={`ai-toggle-btn ${config.isEnabled ? 'active' : 'inactive'}`}
          onClick={handleToggle}
        >
          <span className="toggle-dot"></span>
          {config.isEnabled ? 'AI is Active' : 'AI is Inactive'}
        </button>
      </div>

      {/* Success Toast */}
      {showSuccess && message && (
        <div className="ai-toast success">
          <CheckCircle size={16} />
          <span>{message}</span>
          <button onClick={() => setShowSuccess(false)}><X size={14} /></button>
        </div>
      )}

      {error && (
        <div className="ai-toast error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      <div className="ai-content">
        {/* Info Banner */}
        <div className="ai-info-banner">
          <Info size={18} />
          <div>
            <strong>How AI Works:</strong>
            <p>AI uses your business context, products from your catalog, and FAQ to answer customer questions.</p>
          </div>
        </div>

        {/* Enable Section */}
        <div className="ai-card">
          <div className="ai-card-header">
            <Shield size={20} className="ai-card-icon" />
            <div>
              <h3>Enable AI Assistant</h3>
              <p>Turn on/off AI auto-reply for your WhatsApp messages</p>
            </div>
          </div>
          <div className="ai-card-body">
            <button
              className={`ai-toggle-large ${config.isEnabled ? 'active' : 'inactive'}`}
              onClick={handleToggle}
            >
              <span className="toggle-large-dot"></span>
              {config.isEnabled ? '🟢 AI is Active - Auto-reply is ON' : '🔴 AI is Inactive - Auto-reply is OFF'}
            </button>
            <p className="ai-hint">
              {config.isEnabled 
                ? 'AI will automatically reply to customer messages based on your configuration' 
                : 'Enable AI to start auto-replying to customer messages'}
            </p>
          </div>
        </div>

        {/* Business Context */}
        <div className="ai-card">
          <div className="ai-card-header">
            <MessageSquare size={20} className="ai-card-icon" />
            <div>
              <h3>Business Context</h3>
              <p>Describe your business so AI understands what you do</p>
            </div>
          </div>
          <div className="ai-card-body">
            <textarea
              className="ai-textarea"
              rows={4}
              value={config.businessContext}
              onChange={(e) => setConfig({...config, businessContext: e.target.value})}
              placeholder="Describe your business..."
            />
            <div className="ai-example">
              <Zap size={14} />
              <span>Example: We are a retail business in Tanzania selling electronics, fashion, and home appliances.</span>
            </div>
          </div>
        </div>

        {/* FAQ Text */}
        <div className="ai-card">
          <div className="ai-card-header">
            <MessageSquare size={20} className="ai-card-icon" />
            <div>
              <h3>Frequently Asked Questions</h3>
              <p>List common questions and answers your customers ask</p>
            </div>
          </div>
          <div className="ai-card-body">
            <textarea
              className="ai-textarea"
              rows={8}
              value={config.faqText}
              onChange={(e) => setConfig({...config, faqText: e.target.value})}
              placeholder="Q: What is the price of iPhone 15 Pro?
A: TZS 2,500,000.00

Q: Do you deliver?
A: Yes, we deliver across Dar es Salaam and other major cities in Tanzania.

Q: What payment methods do you accept?
A: We accept M-Pesa, bank transfer (CRDB, NMB, NBC), and cash on delivery.

Q: What is your return policy?
A: You can return products within 7 days of delivery if the product is defective."

            />
            <div className="ai-example">
              <Zap size={14} />
              <span>Format: Q: [question] A: [answer] (one per line)</span>
            </div>
          </div>
        </div>

        {/* Escalation Keywords */}
        <div className="ai-card">
          <div className="ai-card-header">
            <Users size={20} className="ai-card-icon" />
            <div>
              <h3>Escalation Keywords</h3>
              <p>Words that trigger human agent instead of AI</p>
            </div>
          </div>
          <div className="ai-card-body">
            <input
              type="text"
              className="ai-input"
              value={config.escalationKeywords}
              onChange={(e) => setConfig({...config, escalationKeywords: e.target.value})}
              placeholder="agent, human, help, refund, complaint"
            />
            <p className="ai-hint">
              Separate keywords with commas. When customer uses these words, AI will hand over to human.
            </p>
          </div>
        </div>

        {/* Response Language */}
        <div className="ai-card">
          <div className="ai-card-header">
            <Globe size={20} className="ai-card-icon" />
            <div>
              <h3>Response Language</h3>
              <p>Choose language for AI responses</p>
            </div>
          </div>
          <div className="ai-card-body">
            <select
              className="ai-select"
              value={config.responseLanguage}
              onChange={(e) => setConfig({...config, responseLanguage: e.target.value})}
            >
              <option value="auto">🌐 Auto (Same as customer)</option>
              <option value="en">🇬🇧 English</option>
              <option value="sw">🇹🇿 Swahili</option>
            </select>
            <p className="ai-hint">AI will respond in the selected language</p>
          </div>
        </div>

        {/* Save Button */}
        <button
          className="ai-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <span className="ai-save-loading">
              <span className="ai-spinner-small"></span> Saving...
            </span>
          ) : (
            <>
              <Save size={18} />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AiConfig;